import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'

import { keys } from '@/api/keys'
import type { NotificationFilter } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useCurrentUser } from '@/features/auth'
import { NotificationItem, useMarkRead, useNotifications } from '@/features/notifications'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { Link, useSearchParams } from 'react-router-dom'

const TABS: { key: NotificationFilter; label: MessageKey }[] = [
  { key: 'all', label: 'notif.tabAll' },
  { key: 'mentions', label: 'notif.tabMentions' },
  { key: 'follow', label: 'notif.tabFollow' },
]

export function NotificationsPage() {
  const t = useT()
  const { user } = useCurrentUser()
  const pendingRequests = user?.follow_requests_count ?? 0
  // 「我」缓存 5 分钟；进通知页时刷一下，新来的关注请求才能马上出现在顶上
  const client = useQueryClient()
  useEffect(() => {
    void client.invalidateQueries({ queryKey: keys.me() })
  }, [client])
  const [params, setParams] = useSearchParams()
  const filter = (params.get('tab') as NotificationFilter) ?? 'all'
  const query = useNotifications(filter)
  const markRead = useMarkRead()

  const pages = query.data?.pages
  const items = useMemo(() => pages?.flatMap((page) => page.items) ?? [], [pages])
  const newestId = items[0]?.id

  // 本次浏览期间哪些是「新的」。标记已读会让 read 变 true，
  // 只看 read 的话左侧色条会在几百毫秒内消失，用户根本没看见
  const seenUnread = useRef(new Set<string>())
  useEffect(() => {
    for (const item of items) {
      if (!item.read) seenUnread.current.add(item.id)
    }
  }, [items])

  // 打开页面即把看到的这批标为已读。用 up_to_id 而不是「全部已读」，
  // 免得把还没加载出来的更新通知也一起吞掉
  useEffect(() => {
    if (!newestId) return
    if (!items.some((n) => !n.read)) return
    markRead.mutate(newestId)
    // markRead 每次 render 都是新对象，依赖它会死循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestId])

  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  })

  return (
    <>
      <PageHeader title={t('nav.notifications')}>
        <nav className="flex" aria-label={t('notif.filterLabel')}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setParams(tab.key === 'all' ? {} : { tab: tab.key })}
              aria-current={filter === tab.key ? 'page' : undefined}
              className={cn(
                'flex-1 py-4 text-[15px] transition-colors hover:bg-surface-hover',
                filter === tab.key ? 'font-bold' : 'text-ink-muted',
              )}
            >
              <span
                className={cn(
                  'inline-block pb-3',
                  filter === tab.key && 'border-b-4 border-brand-ink',
                )}
              >
                {t(tab.label)}
              </span>
            </button>
          ))}
        </nav>
      </PageHeader>

      {/* 关注请求不占通知的类型（D76），有待处理的就在最上面提一句 */}
      {pendingRequests > 0 && (
        <Link
          to="/follow-requests"
          className="block border-b border-line bg-brand/10 px-4 py-3 text-[15px] font-bold hover:bg-brand/20"
        >
          {t('followRequests.banner', { count: pendingRequests })}
        </Link>
      )}

      {query.isPending && <PostListSkeleton count={5} />}
      {query.isError && (
        <ErrorState message={query.error.message} onRetry={() => query.refetch()} />
      )}

      {!query.isPending && items.length === 0 && (
        <EmptyState title={t('notif.emptyTitle')} description={t('notif.emptyBody')} />
      )}

      {items.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          highlight={!notification.read || seenUnread.current.has(notification.id)}
        />
      ))}

      <div ref={sentinelRef} aria-hidden className="h-px" />
      {query.isFetchingNextPage && <CenteredSpinner />}
    </>
  )
}
